// Pure sizing/geometry helpers for the flyer maker. The export is rendered from a fixed-size offscreen artboard,
// so the output is the same at any viewport.

export type PaperId = "letter" | "a4" | "a5" | "half-letter";

export interface PaperSpec { id: PaperId; label: string; wIn: number; hIn: number }

export const PAPERS: PaperSpec[] = [
  { id: "letter", label: "Letter (8.5 x 11 in)", wIn: 8.5, hIn: 11 },
  { id: "a4", label: "A4 (210 x 297 mm)", wIn: 210 / 25.4, hIn: 297 / 25.4 },
  { id: "a5", label: "A5 (148 x 210 mm)", wIn: 148 / 25.4, hIn: 210 / 25.4 },
  { id: "half-letter", label: "Half letter (5.5 x 8.5 in)", wIn: 5.5, hIn: 8.5 },
];

export const CSS_DPI = 96;
export const EXPORT_DPI = 300;
export const BLEED_IN = 0.125;
/** Safe zone: keep text this far inside the trim edge. */
export const SAFE_IN = 0.25;
/** Width of the Letter artboard in CSS px; type sizes are designed at this width and scaled for other papers. */
export const BASE_WIDTH_PX = 816;

export function paperById(id: string): PaperSpec {
  return PAPERS.find((p) => p.id === id) ?? PAPERS[0];
}

export interface ExportSpec {
  /** Artboard size in CSS px */
  cssW: number; cssH: number;
  /** Exported picture size in px (paper at 300 dpi) */
  pxW: number; pxH: number;
  /** pixelRatio that turns the artboard into exactly pxW x pxH */
  ratio: number;
  /** Type scale relative to the Letter design */
  k: number;
}

export function exportSpec(p: PaperSpec): ExportSpec {
  const cssW = Math.round(p.wIn * CSS_DPI);
  const cssH = Math.round(p.hIn * CSS_DPI);
  const pxW = Math.round(p.wIn * EXPORT_DPI);
  const pxH = Math.round(p.hIn * EXPORT_DPI);
  return { cssW, cssH, pxW, pxH, ratio: pxW / cssW, k: cssW / BASE_WIDTH_PX };
}

/** Print-shop PDF page boxes in PDF points: MediaBox = paper + bleed on every side, TrimBox = paper. */
export function bleedBoxes(p: PaperSpec, bleedIn = BLEED_IN) {
  const trimW = p.wIn * 72, trimH = p.hIn * 72, b = bleedIn * 72;
  return {
    media: { w: trimW + 2 * b, h: trimH + 2 * b },
    trim: { x: b, y: b, w: trimW, h: trimH },
  };
}

/** Plain PDF page size in points. */
export function pageSizePt(p: PaperSpec): [number, number] {
  return [p.wIn * 72, p.hIn * 72];
}

/** File name base from the business name: [a-z0-9-], fallback "flyer". */
export function flyerSlug(name: string, fallback = "flyer"): string {
  const s = (name || "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/g, "");
  return s || fallback;
}

/**
 * Headline font size in px (Letter design scale) so the text fits in at most `maxLines` lines. Bounded: at most 30 steps,
 * no DOM measurement, so there is nothing to loop on.
 */
export function fitHeadlineSize(text: string, widthPx: number, maxLines = 3, maxSize = 36, minSize = 18): number {
  const len = Math.max(1, (text || "").length);
  let size = maxSize;
  for (let i = 0; i < 30 && size > minSize; i++) {
    const charsPerLine = Math.max(1, Math.floor(widthPx / (size * 0.55)));
    if (Math.ceil(len / charsPerLine) <= maxLines) break;
    size -= 1;
  }
  return Math.max(minSize, size);
}

/** True when content is taller than the space it has. A 1 px tolerance avoids rounding noise. */
export function overflows(scrollHeight: number, clientHeight: number): boolean {
  return scrollHeight - clientHeight > 1;
}

/** Scale that fits an artboard into the available width (never enlarges; clamped to a sane minimum, rounded to avoid resize loops). */
export function previewScale(availableW: number, artboardW: number): number {
  if (!(availableW > 0) || !(artboardW > 0)) return 1;
  return Math.round(Math.max(0.1, Math.min(1, availableW / artboardW)) * 1000) / 1000;
}
